import { useBlockProps, InspectorControls, BlockControls } from "@wordpress/block-editor";
import { PanelBody, SelectControl, TextControl, TextareaControl, Button, ButtonGroup, ToggleControl, Disabled, ToolbarGroup, ToolbarDropdownMenu } from "@wordpress/components";
import { useSelect } from "@wordpress/data";
import DebouncedServerSideRender from "../../../shared/js/DebouncedServerSideRender";
import { __, sprintf } from "@wordpress/i18n";
import { isDuplicateTocAnchor } from "../../../shared/js/tocAnchorDiagnostics";

const VARIANT_OPTIONS = [
    { label: __("Default", "content-egg"), value: "default" },
    { label: __("Checklist", "content-egg"), value: "checklist" },
    { label: __("Cards", "content-egg"), value: "cards" },
    { label: __("Card Grid", "content-egg"), value: "card-grid" },
];

const COLOR_SCHEME_OPTIONS = [
    { label: __("Auto (site default)", "content-egg"), value: "auto" },
    { label: __("Light", "content-egg"), value: "light" },
    { label: __("Dark", "content-egg"), value: "dark" },
];

export default function Edit({ attributes, setAttributes }) {
    const blockProps = useBlockProps({ className: attributes.align ? `align${attributes.align}` : "" });
    const { variant, section_label, title, heading_tag, steps = [], note, anchor, toc_label, include_in_toc, level, color_scheme } = attributes;
    const allBlocks = useSelect((select) => select("core/block-editor").getBlocks(), []);
    const hasDuplicateTocAnchor = !!include_in_toc && isDuplicateTocAnchor(allBlocks, anchor);
    const suggestedTocLabel = (title || section_label || "").trim();

    const updateSteps = (nextSteps) => setAttributes({ steps: nextSteps });
    const addStep = () => updateSteps([...(steps || []), { title: "", description: "" }]);
    const updateStep = (index, key, value) => {
        const next = [...(steps || [])];
        next[index] = { title: "", description: "", ...(next[index] || {}), [key]: value };
        updateSteps(next);
    };
    const removeStep = (index) => {
        const next = [...(steps || [])];
        next.splice(index, 1);
        updateSteps(next);
    };

    const hasPreviewContent = (steps || []).some((item) => (item?.title || "").trim() !== "" || (item?.description || "").trim() !== "");

    return (
        <>
            <BlockControls>
                <ToolbarGroup>
                    <ToolbarDropdownMenu
                        icon="screenoptions"
                        label={__("Variant", "content-egg")}
                        text={VARIANT_OPTIONS.find((o) => o.value === variant)?.label}
                        controls={VARIANT_OPTIONS.map((option) => ({
                            title: option.label,
                            isActive: variant === option.value,
                            onClick: () => setAttributes({ variant: option.value }),
                        }))}
                    />
                </ToolbarGroup>
            </BlockControls>

            <InspectorControls>
                <PanelBody title={__("Layout", "content-egg")} initialOpen={true}>
                    <SelectControl
                        label={__("Variant", "content-egg")}
                        value={variant}
                        options={VARIANT_OPTIONS}
                        onChange={(value) => setAttributes({ variant: value })}
                        __nextHasNoMarginBottom
                    />
                    <SelectControl
                        label={__("Color Scheme", "content-egg")}
                        value={color_scheme}
                        options={COLOR_SCHEME_OPTIONS}
                        onChange={(value) => setAttributes({ color_scheme: value })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>

                <PanelBody title={__("Content", "content-egg")} initialOpen={true}>
                    <TextControl
                        label={__("Section Label", "content-egg")}
                        value={section_label}
                        onChange={(value) => setAttributes({ section_label: value })}
                    />
                    <TextControl
                        label={__("Title", "content-egg")}
                        value={title}
                        onChange={(value) => setAttributes({ title: value })}
                    />
                    <div style={{ marginBottom: "8px" }}>
                        <div style={{ fontSize: "11px", fontWeight: 500, marginBottom: "6px" }}>
                            {__("Heading Tag", "content-egg")}
                        </div>
                        <ButtonGroup>
                            {["h1", "h2", "h3", "h4", "div"].map((tag) => (
                                <Button
                                    key={tag}
                                    variant={heading_tag === tag ? "primary" : "secondary"}
                                    size="small"
                                    onClick={() => setAttributes({ heading_tag: tag })}
                                >
                                    {tag.toUpperCase()}
                                </Button>
                            ))}
                        </ButtonGroup>
                    </div>
                    <TextareaControl
                        label={__("Note", "content-egg")}
                        value={note}
                        onChange={(value) => setAttributes({ note: value })}
                        rows={2}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>

                <PanelBody title={__("Steps", "content-egg")} initialOpen={false}>
                    {(steps || []).map((item, index) => (
                        <div
                            key={index}
                            style={{
                                marginBottom: index === steps.length - 1 ? "0" : "10px",
                                paddingBottom: index === steps.length - 1 ? "0" : "10px",
                                borderBottom: index === steps.length - 1 ? "none" : "1px solid #e0e0e0",
                            }}
                        >
                            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "6px" }}>
                                <Button
                                    icon="no-alt"
                                    isDestructive
                                    size="small"
                                    onClick={() => removeStep(index)}
                                    label={__("Remove", "content-egg")}
                                />
                            </div>
                            <TextControl
                                label={__("Title", "content-egg")}
                                value={item?.title || ""}
                                onChange={(value) => updateStep(index, "title", value)}
                            />
                            <TextareaControl
                                label={__("Description", "content-egg")}
                                value={item?.description || ""}
                                onChange={(value) => updateStep(index, "description", value)}
                                rows={3}
                                __nextHasNoMarginBottom
                            />
                        </div>
                    ))}
                    <Button variant="secondary" size="small" onClick={addStep} style={{ marginTop: "10px" }}>
                        {__("Add step", "content-egg")}
                    </Button>
                </PanelBody>

                <PanelBody title={__("TOC", "content-egg")} initialOpen={false}>
                    <TextControl
                        label={__("Anchor", "content-egg")}
                        value={anchor || ""}
                        onChange={(value) => setAttributes({ anchor: value })}
                    />
                    {hasDuplicateTocAnchor && (
                        <p style={{ marginTop: "-4px", marginBottom: "12px", color: "#8a1f11", fontSize: "12px" }}>
                            {__("This anchor is already used by another TOC block.", "content-egg")}
                        </p>
                    )}
                    <ToggleControl
                        label={__("Include In TOC", "content-egg")}
                        checked={!!include_in_toc}
                        onChange={(value) => setAttributes({ include_in_toc: value })}
                    />
                    {include_in_toc && !(anchor || "").trim() && (
                        <p style={{ marginTop: "-4px", marginBottom: "12px", color: "#757575", fontSize: "12px" }}>
                            {__("Set an anchor so the TOC can link to this block.", "content-egg")}
                        </p>
                    )}
                    <TextControl
                        label={__("TOC Label", "content-egg")}
                        value={toc_label || ""}
                        onChange={(value) => setAttributes({ toc_label: value })}
                    />
                    {include_in_toc && !(toc_label || "").trim() && (
                        <p style={{ marginTop: "-4px", marginBottom: "12px", color: "#757575", fontSize: "12px" }}>
                            {suggestedTocLabel
                                ? sprintf(__("Suggested label: %s", "content-egg"), `"${suggestedTocLabel}"`)
                                : __("Set a TOC label so this block can appear in automatic TOC mode.", "content-egg")}
                        </p>
                    )}
                    <TextControl
                        label={__("Level", "content-egg")}
                        type="number"
                        min={1}
                        max={6}
                        value={level ?? 2}
                        onChange={(value) => setAttributes({ level: value === "" ? 2 : Number(value) })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>
            </InspectorControls>

            <div {...blockProps}>
                {hasPreviewContent ? (
                    <Disabled>
                        <DebouncedServerSideRender
                            block="eggb/step-list"
                            attributes={attributes}
                        />
                    </Disabled>
                ) : (
                    <p style={{ color: "#999", fontStyle: "italic", padding: "16px", margin: 0, fontSize: "13px" }}>
                        {__("Add steps in the sidebar.", "content-egg")}
                    </p>
                )}
            </div>
        </>
    );
}
